<script setup lang="ts">
import EntityCardShell from '@/components/entity/EntityCardShell.vue';
import EntityActionButton from '@/components/entity/EntityActionButton.vue';
import { Button } from '@/components/ui/button';
import { ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from '@/components/ui/context-menu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { formatMoney } from '~/lib/formatters';
import { quoteWasSharedWithClient, resolveQuoteAcceptanceSource } from '~/lib/quoteLifecycle';
import type { Invoice, Quote } from '~/types/models';
import { BriefcaseBusiness, Building2, Check, CheckCircle2, CircleDollarSign, Copy, Eye, Mail, MoreHorizontal, Pencil, ReceiptText, Share2, Trash2, X } from 'lucide-vue-next';

const props = withDefaults(defineProps<{
    quote: Quote;
    showProject?: boolean;
    showClient?: boolean;
    canView?: boolean;
    canEdit?: boolean;
    canSend?: boolean;
    canAccept?: boolean;
    canReject?: boolean;
    canGenerateInvoices?: boolean;
    canDelete?: boolean;
    canCopyPublicLink?: boolean;
    projectName?: string;
    clientName?: string;
    invoices?: Invoice[];
    surface?: 'default' | 'embedded' | 'list';
}>(), {
    showProject: true,
    showClient: true,
    canView: true,
    canEdit: false,
    canSend: false,
    canAccept: false,
    canReject: false,
    canGenerateInvoices: false,
    canDelete: false,
    canCopyPublicLink: false,
    projectName: '',
    clientName: '',
    invoices: () => [],
    surface: 'default',
});

const emit = defineEmits<{
    view: [Quote];
    edit: [Quote];
    send: [string];
    remind: [string];
    accept: [string];
    'approve-internally': [string];
    reject: [string];
    'view-linked-invoice': [Invoice];
    'create-full-invoice': [string];
    'create-deposit-invoice': [string];
    'create-balance-invoice': [string];
    'create-next-installment-invoice': [string];
    'copy-public-link': [Quote];
    delete: [string];
    details: [Quote];
}>();

function journeyBadgeLabel(quote: Quote): string {
    if (quote.status === 'accepted') return resolveQuoteAcceptanceSource(quote) === 'internal' ? 'Internally approved' : 'Client-approved';
    if (quote.status === 'sent') return 'Awaiting client response';
    if (quote.status === 'rejected') return quote.clientFacing === false ? 'Rejected internally' : 'Client rejected';
    if (quote.status === 'expired') return 'Needs review';
    return quote.clientFacing === false ? 'Internal draft' : 'Draft';
}

function sharingBadgeLabel(quote: Quote): string {
    if (!quoteWasSharedWithClient(quote)) return 'Internal only';
    if (quote.status === 'sent') return 'Shared with client';
    if (quote.status === 'accepted') return 'Accepted by client';
    if (quote.status === 'rejected') return 'Was client-facing';
    return 'Client-facing';
}

function hasDeposit(quote: Quote): boolean {
    return Boolean(quote.deposit?.enabled && Number(quote.deposit.amount || 0) > 0);
}

function hasPaymentPlan(quote: Quote): boolean {
    return Boolean(quote.paymentPlan?.enabled && (quote.paymentPlan.installments?.length || 0) > 0);
}

function showFullInvoiceAction(quote: Quote): boolean {
    return !hasDeposit(quote) && !hasPaymentPlan(quote);
}

function showDepositInvoiceAction(quote: Quote): boolean {
    return hasDeposit(quote) && !hasPaymentPlan(quote);
}

function showBalanceInvoiceAction(quote: Quote): boolean {
    return hasDeposit(quote) && !hasPaymentPlan(quote);
}

function showNextInstallmentAction(quote: Quote): boolean {
    return hasPaymentPlan(quote);
}

function paidAmount(invoice: Invoice): number {
    if (invoice.status === 'paid') return Number(invoice.amountTotal || 0);
    return Number(invoice.amountPaid || 0);
}

function canDeleteQuote(quote: Quote): boolean {
    return props.invoices.length === 0;
}

function paidInvoiceCount(): number {
    return props.invoices.filter(invoice => invoice.status === 'paid').length;
}

function unpaidInvoiceCount(): number {
    return props.invoices.filter(invoice => invoice.status !== 'paid').length;
}

function primaryActionLabel(quote: Quote): string {
    if (quote.status === 'draft') return 'Send to client';
    if (quote.status === 'sent') return 'Mark accepted';
    if (quote.status === 'accepted' && showDepositInvoiceAction(quote)) return 'Create deposit invoice';
    if (quote.status === 'accepted' && showBalanceInvoiceAction(quote)) return 'Create balance invoice';
    if (quote.status === 'accepted' && showNextInstallmentAction(quote)) return 'Create next installment';
    if (quote.status === 'accepted' && showFullInvoiceAction(quote)) return 'Create full invoice';
    return 'View quote';
}

function triggerPrimaryAction(quote: Quote) {
    if (quote.status === 'draft' && props.canSend) {
        emit('send', quote.id);
        return;
    }
    if (quote.status === 'sent' && props.canAccept) {
        emit('accept', quote.id);
        return;
    }
    if (quote.status === 'accepted' && props.canGenerateInvoices) {
        if (showDepositInvoiceAction(quote)) return emit('create-deposit-invoice', quote.id);
        if (showBalanceInvoiceAction(quote)) return emit('create-balance-invoice', quote.id);
        if (showNextInstallmentAction(quote)) return emit('create-next-installment-invoice', quote.id);
        if (showFullInvoiceAction(quote)) return emit('create-full-invoice', quote.id);
    }
    emit('view', quote);
}

const quoteInitials = computed(() => {
    const source = String(props.quote.title || props.quote.quoteNumber || 'Quote').trim();
    return source
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase() || 'QT';
});

const subtitle = computed(() => {
    const parts = [
        props.quote.quoteNumber || 'No quote number',
        props.quote.issueDate ? `Issued ${props.quote.issueDate}` : '',
        props.quote.expiryDate ? `Expires ${props.quote.expiryDate}` : '',
    ].filter(Boolean);
    return parts.join(' • ');
});

const fallbackClass = 'bg-muted [&>span]:text-muted-foreground';
</script>

<template>
    <EntityCardShell
        :title="quote.title || quote.quoteNumber || 'Quote'"
        :subtitle="subtitle"
        :activate-on-click="canView"
        :fallback-text="quoteInitials"
        :fallback-class="fallbackClass"
        :surface="surface"
        @activate="emit('details', quote)"
    >
        <template #mobile-actions>
            <Button v-if="canView" size="sm" variant="outline" @click="emit('details', quote)">
                <Eye class="mr-2 size-4" />
                View details
            </Button>
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action" aria-label="More quote actions">
                        <MoreHorizontal class="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem v-if="canView" @click="emit('view', quote)"><Eye class="mr-2 size-4" />View quote</DropdownMenuItem>
                    <DropdownMenuItem v-if="canCopyPublicLink" @click="emit('copy-public-link', quote)"><Copy class="mr-2 size-4" />Copy public link</DropdownMenuItem>
                    <DropdownMenuItem v-if="canEdit && quote.status !== 'accepted'" @click="emit('edit', quote)"><Pencil class="mr-2 size-4" />Edit</DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && quote.status === 'draft'" @click="emit('send', quote.id)"><Mail class="mr-2 size-4" />Send to client</DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && quote.status === 'sent'" @click="emit('remind', quote.id)"><Mail class="mr-2 size-4" />Send reminder</DropdownMenuItem>
                    <DropdownMenuItem v-if="canAccept && canGenerateInvoices && quote.status === 'draft'" @click="emit('approve-internally', quote.id)"><Check class="mr-2 size-4" />Approve internally</DropdownMenuItem>
                    <DropdownMenuItem v-if="canAccept && quote.status === 'sent'" @click="emit('accept', quote.id)"><Check class="mr-2 size-4" />Mark accepted</DropdownMenuItem>
                    <DropdownMenuItem v-if="canReject && quote.status === 'sent'" class="text-destructive focus:text-destructive" @click="emit('reject', quote.id)"><X class="mr-2 size-4" />Reject</DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canGenerateInvoices && quote.status === 'accepted'" />
                    <DropdownMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showFullInvoiceAction(quote)" @click="emit('create-full-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create full invoice</DropdownMenuItem>
                    <DropdownMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showDepositInvoiceAction(quote)" @click="emit('create-deposit-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create deposit invoice</DropdownMenuItem>
                    <DropdownMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showBalanceInvoiceAction(quote)" @click="emit('create-balance-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create balance invoice</DropdownMenuItem>
                    <DropdownMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showNextInstallmentAction(quote)" @click="emit('create-next-installment-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create next installment</DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canDelete" />
                    <DropdownMenuItem v-if="canDelete" :disabled="!canDeleteQuote(quote)" class="text-destructive focus:text-destructive" @click="emit('delete', quote.id)"><Trash2 class="mr-2 size-4" />Delete</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #actions>
            <EntityActionButton
                v-if="canView"
                label="View quote details"
                :icon="Eye"
                @click="emit('details', quote)"
            />
            <EntityActionButton
                v-if="(quote.status === 'draft' && canSend) || (quote.status === 'sent' && canAccept) || (quote.status === 'accepted' && canGenerateInvoices)"
                :label="primaryActionLabel(quote)"
                :icon="quote.status === 'accepted' ? ReceiptText : quote.status === 'draft' ? Mail : Check"
                @click="triggerPrimaryAction(quote)"
            />
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action" aria-label="More quote actions">
                        <MoreHorizontal class="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem v-if="canView" @click="emit('view', quote)"><Eye class="mr-2 size-4" />View quote</DropdownMenuItem>
                    <DropdownMenuItem v-if="canCopyPublicLink" @click="emit('copy-public-link', quote)"><Copy class="mr-2 size-4" />Copy public link</DropdownMenuItem>
                    <DropdownMenuItem v-if="canEdit && quote.status !== 'accepted'" @click="emit('edit', quote)"><Pencil class="mr-2 size-4" />Edit</DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && quote.status === 'draft'" @click="emit('send', quote.id)"><Mail class="mr-2 size-4" />Send to client</DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && quote.status === 'sent'" @click="emit('remind', quote.id)"><Mail class="mr-2 size-4" />Send reminder</DropdownMenuItem>
                    <DropdownMenuItem v-if="canAccept && canGenerateInvoices && quote.status === 'draft'" @click="emit('approve-internally', quote.id)"><Check class="mr-2 size-4" />Approve internally</DropdownMenuItem>
                    <DropdownMenuItem v-if="canAccept && quote.status === 'sent'" @click="emit('accept', quote.id)"><Check class="mr-2 size-4" />Mark accepted</DropdownMenuItem>
                    <DropdownMenuItem v-if="canReject && quote.status === 'sent'" class="text-destructive focus:text-destructive" @click="emit('reject', quote.id)"><X class="mr-2 size-4" />Reject</DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canGenerateInvoices && quote.status === 'accepted'" />
                    <DropdownMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showFullInvoiceAction(quote)" @click="emit('create-full-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create full invoice</DropdownMenuItem>
                    <DropdownMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showDepositInvoiceAction(quote)" @click="emit('create-deposit-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create deposit invoice</DropdownMenuItem>
                    <DropdownMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showBalanceInvoiceAction(quote)" @click="emit('create-balance-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create balance invoice</DropdownMenuItem>
                    <DropdownMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showNextInstallmentAction(quote)" @click="emit('create-next-installment-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create next installment</DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canDelete" />
                    <DropdownMenuItem v-if="canDelete" :disabled="!canDeleteQuote(quote)" class="text-destructive focus:text-destructive" @click="emit('delete', quote.id)"><Trash2 class="mr-2 size-4" />Delete</DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #body>
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                    <span class="inline-flex items-center gap-1.5 capitalize">
                        <span
                            class="size-2 rounded-full"
                            :class="quote.status === 'accepted' ? 'bg-emerald-500' : quote.status === 'rejected' || quote.status === 'expired' ? 'bg-destructive' : 'bg-muted-foreground/50'"
                        />
                        {{ quote.status }}
                    </span>
                    <span class="inline-flex items-center gap-1.5">
                        <Share2 class="size-3.5" />
                        {{ journeyBadgeLabel(quote) }} · {{ sharingBadgeLabel(quote) }}
                    </span>
                    <span v-if="showClient" class="inline-flex items-center gap-1.5">
                        <Building2 class="size-3.5" />
                        {{ clientName }}
                    </span>
                    <span v-if="showProject && quote.projectId" class="inline-flex items-center gap-1.5">
                        <BriefcaseBusiness class="size-3.5" />
                        {{ projectName || quote.projectId }}
                    </span>
                    <span class="inline-flex items-center gap-1.5" title="Paid invoices">
                        <CheckCircle2 class="size-3.5" />
                        {{ paidInvoiceCount() }} paid
                    </span>
                    <span class="inline-flex items-center gap-1.5" title="Outstanding invoices">
                        <CircleDollarSign class="size-3.5" />
                        {{ unpaidInvoiceCount() }} outstanding
                    </span>
                </div>

                <p class="shrink-0 text-right text-lg font-semibold text-foreground">
                    {{ formatMoney(quote.amountTotal, quote.currency || 'USD') }}
                </p>
            </div>
        </template>

        <template #context-menu>
            <ContextMenuContent class="w-56">
                <ContextMenuItem v-if="canView" @click="emit('view', quote)"><Eye class="mr-2 size-4" />View quote</ContextMenuItem>
                <ContextMenuItem v-if="canEdit && quote.status !== 'accepted'" @click="emit('edit', quote)"><Pencil class="mr-2 size-4" />Edit</ContextMenuItem>
                <ContextMenuItem v-if="canSend && quote.status === 'draft'" @click="emit('send', quote.id)"><Mail class="mr-2 size-4" />Send to client</ContextMenuItem>
                <ContextMenuItem v-if="canSend && quote.status === 'sent'" @click="emit('remind', quote.id)"><Mail class="mr-2 size-4" />Send reminder</ContextMenuItem>
                <ContextMenuItem v-if="canAccept && canGenerateInvoices && quote.status === 'draft'" @click="emit('approve-internally', quote.id)"><Check class="mr-2 size-4" />Approve internally</ContextMenuItem>
                <ContextMenuItem v-if="canAccept && quote.status === 'sent'" @click="emit('accept', quote.id)"><Check class="mr-2 size-4" />Mark accepted</ContextMenuItem>
                <ContextMenuItem v-if="canReject && quote.status === 'sent'" class="text-destructive focus:text-destructive" @click="emit('reject', quote.id)"><X class="mr-2 size-4" />Reject</ContextMenuItem>
                <ContextMenuSeparator v-if="canGenerateInvoices && quote.status === 'accepted'" />
                <ContextMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showFullInvoiceAction(quote)" @click="emit('create-full-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create full invoice</ContextMenuItem>
                <ContextMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showDepositInvoiceAction(quote)" @click="emit('create-deposit-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create deposit invoice</ContextMenuItem>
                <ContextMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showBalanceInvoiceAction(quote)" @click="emit('create-balance-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create balance invoice</ContextMenuItem>
                <ContextMenuItem v-if="canGenerateInvoices && quote.status === 'accepted' && showNextInstallmentAction(quote)" @click="emit('create-next-installment-invoice', quote.id)"><ReceiptText class="mr-2 size-4" />Create next installment</ContextMenuItem>
                <ContextMenuSeparator v-if="canDelete" />
                <ContextMenuItem v-if="canDelete" :disabled="!canDeleteQuote(quote)" class="text-destructive focus:text-destructive" @click="emit('delete', quote.id)"><Trash2 class="mr-2 size-4" />Delete</ContextMenuItem>
            </ContextMenuContent>
        </template>
    </EntityCardShell>
</template>
